import { pipe } from 'fp-ts/function';
import { filterMap } from 'fp-ts/Array';
import { option } from 'fp-ts';
import { useMemo } from 'react';
import {
    Button,
    IconName,
    Stack,
    Table,
    TableActiveColumnFilters,
    ActionToolbarButton,
    ActionToolbar,
    TableHeader,
    TableProvider,
    useRowSelection,
    useTableState,
} from '@optimus-web/core';
import { RoleGetResult, UserProfileFilters, UserProfileResult } from '@mews-commander/data-access';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useRemoveRoleUsers, useRoleUsersData } from './queries';
import { AssignUsersDialog } from './AssignUsersDialog';
import { useColumns } from './tableMapping';

const useProvisionedUserIds = (users?: Pick<UserProfileResult, 'isScimProvisioned' | 'userId'>[]): string[] => {
    if (!users?.length) {
        return [];
    }

    return pipe(
        users,
        filterMap((user) => (user.isScimProvisioned ? option.some(user.userId) : option.none))
    );
};

export const AssignUsersTab = ({ role }: { role: RoleGetResult }) => {
    const [tableState, setTableState] = useTableState<UserProfileResult, UserProfileFilters>();
    const {
        rowSelection,
        setRowSelection,
        selectedRowsIds,
        selectedRowsCount,
        hasRowsSelected,
        unselectRow,
        resetRowSelection,
    } = useRowSelection();

    const { data, isFetching } = useRoleUsersData(tableState, role.id);
    const users = useMemo(() => data?.data || [], [data]);
    const totalRowCount = data?.totalRowCount ?? 0;

    const removeUsers = useRemoveRoleUsers(role.name);
    const columns = useColumns({ role, onRowDelete: unselectRow });

    const [assignUsersDialogProps, toggleAssignUsersDialog] = useDialog();

    const provisionedUserIds: string[] = useProvisionedUserIds(users);

    return (
        <Stack vertical>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
                <TableHeader
                    title={<LocalizedText textKey={TextKey.UserPlural} />}
                    subTitle={totalRowCount.toString()}
                    globalSearch={{ placeholder: <LocalizedText textKey={TextKey.UsersSearchHint} /> }}
                    rightSide={
                        <Button
                            variant="tertiary"
                            icon={IconName.PlusSimple}
                            onClick={toggleAssignUsersDialog}
                            type="submit"
                        >
                            <LocalizedText textKey={TextKey.RoleEditAssignButton} />
                        </Button>
                    }
                />

                <TableActiveColumnFilters />

                <ActionToolbar
                    disabled={!hasRowsSelected}
                    leftSide={
                        hasRowsSelected ? (
                            <LocalizedText textKey={TextKey.SelectedItems} substitutes={{ count: selectedRowsCount }} />
                        ) : null
                    }
                    rightSide={
                        <ActionToolbarButton
                            icon={IconName.Delete}
                            disabled={!hasRowsSelected}
                            onClick={() => {
                                removeUsers.mutate(
                                    { userIds: selectedRowsIds, roleId: role.id },
                                    { onSuccess: () => resetRowSelection() }
                                );
                            }}
                        >
                            <LocalizedText textKey={TextKey.Remove} />
                        </ActionToolbarButton>
                    }
                />

                <Table
                    data={users}
                    amountOfRows={totalRowCount}
                    loading={isFetching}
                    uniqueRowId="userId"
                    rowSelection={{
                        rowSelection,
                        onRowSelectionChange: setRowSelection,
                        enableRowSelection: (row) => !provisionedUserIds.includes(row.original.userId),
                    }}
                    fixedFirstColumn
                />
            </TableProvider>

            <AssignUsersDialog role={role} {...assignUsersDialogProps} />
        </Stack>
    );
};
